/**
 * ZAR-1762 / TG-2 — "Tips & Gratuity" drill-in.
 *
 * One component rendered by Snapshot, Sales and EOD Reconciliation (desktop and
 * mobile). Two sections, always both present, never a toggle between them: a manager
 * reconciling a shift's payout has to see both pools at once. Section 2 is a
 * structural clone of section 1 with gratuity-specific denominators.
 *
 * Purely presentational — every number arrives pre-computed in `data`
 * (see tipsGratuityData.ts). Surfaces own their fetching and their source toggle.
 */
import { T, Bone } from '../../reportTokens';
import SectionError from '../../SectionError';
import SectionEmpty from '../../SectionEmpty';
import KpiTooltip from '../../../KpiTooltip';
import { tgSubHeader, type TgSection, type TipsGratuityData } from './tipsGratuityData';

interface Props {
    data: TipsGratuityData;
    /** Surface's own currency formatter (country-aware symbol). */
    fmt: (n: number) => string;
    isMobile?: boolean;
    /** Totals + staff rankings are still resolving. */
    loading?: boolean;
    staffFailure?: boolean;
    onRetryStaff?: () => void;
    channelLoading?: boolean;
    channelFailure?: boolean;
    onRetryChannel?: () => void;
    /** House-retained outlets show the Tips section only; gratuity is revenue there. */
    includeGratuity?: boolean;
    /** Surfaces with no per-channel tip/gratuity attribution hide both channel panels. */
    showChannels?: boolean;
}

const fmtRate = (pct: number | null): string => (pct == null ? '—' : `${pct.toFixed(1)}%`);

const TipsGratuityDrill = ({
    data,
    fmt,
    isMobile = false,
    loading = false,
    staffFailure = false,
    onRetryStaff,
    channelLoading = false,
    channelFailure = false,
    onRetryChannel,
    includeGratuity = true,
    showChannels = true,
}: Props) => {
    const s = isMobile
        ? { label: 10, value: 16, sub: 8, title: 12, subtitle: 10, row: 11, rowVal: 12, bar: 5, pad: 12, gap: 8, radius: 12 }
        : { label: 11, value: 22, sub: 10, title: 13, subtitle: 11, row: 12, rowVal: 12, bar: 6, pad: 16, gap: 12, radius: 12 };

    const card: React.CSSProperties = {
        background: T.card,
        border: `1px solid ${T.cardBorder}`,
        borderRadius: s.radius,
        boxShadow: T.cardShadow,
    };

    const MiniK = ({ label, value, color, sub }: { label: string; value: string; color?: string; sub?: string }) => (
        <div style={{ ...card, flex: 1, minWidth: isMobile ? 0 : 120, padding: isMobile ? '8px 10px' : '12px 14px', textAlign: isMobile ? 'center' : 'left' }}>
            <div style={{ fontSize: s.label, fontWeight: 500, color: T.t3, textTransform: 'uppercase', letterSpacing: 0.5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</div>
            <div style={{ fontSize: s.value, fontWeight: 700, color: color ?? T.t1, marginTop: 3, overflowWrap: 'anywhere' }}>
                {loading ? <Bone w={isMobile ? 56 : 80} h={s.value} r={4} /> : value}
            </div>
            {!loading && sub && <div style={{ fontSize: s.sub, color: T.t3, marginTop: 2 }}>{sub}</div>}
        </div>
    );

    const SectionBanner = ({ text }: { text: string }) => (
        <div style={{ fontSize: isMobile ? 11 : 12, fontWeight: 700, color: T.accent, textTransform: 'uppercase', letterSpacing: 1, margin: isMobile ? '14px 0 8px' : '20px 0 10px' }}>
            {text}
        </div>
    );

    const StaffPanel = ({
        title,
        subtitle,
        rows,
        barColor,
        showRate,
        emptyName,
    }: {
        title: string;
        subtitle: string;
        rows: TgSection['staff'];
        barColor: string;
        showRate: boolean;
        emptyName: string;
    }) => {
        // Top earner sets the full bar. No artificial floor of 1 — that would shrink a
        // lone tiny amount (e.g. $0.02) to a ~0%-wide bar.
        const max = rows[0]?.amount || 0;
        return (
            <div style={{ ...card, flex: isMobile ? undefined : 1.35, padding: s.pad, marginBottom: isMobile ? s.gap : 0 }}>
                <div style={{ fontSize: s.title, fontWeight: 600, color: T.t1, marginBottom: 2 }}>{title}</div>
                <div style={{ fontSize: s.subtitle, color: T.t3, marginBottom: 12 }}>{subtitle}</div>
                {loading ? (
                    <div>
                        {[1, 2, 3, 4].map((i) => (
                            <div key={i} style={{ marginBottom: i < 4 ? 10 : 0 }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                                    <Bone h={12} r={3} style={{ width: `${45 + i * 10}%` }} />
                                    <Bone w={44} h={12} r={3} />
                                </div>
                                <Bone h={s.bar} r={3} />
                            </div>
                        ))}
                    </div>
                ) : staffFailure ? (
                    <SectionError kpiName={emptyName} onRetry={onRetryStaff} size="small" />
                ) : rows.length ? (
                    rows.map((r, i) => (
                        <div key={`${r.name}-${i}`} style={{ marginBottom: i < rows.length - 1 ? 10 : 0 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 3, gap: 8 }}>
                                <span style={{ display: 'flex', alignItems: 'center', gap: 5, minWidth: 0 }}>
                                    <span style={{ fontSize: s.row, fontWeight: 500, color: T.t1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.name}</span>
                                    {/* REF-6 — tip refunded after the tip period closed: surfaced, never hidden. */}
                                    {!!r.refundedAfterTipOut && (
                                        <span style={{ display: 'inline-flex', alignItems: 'center', color: T.orange, flexShrink: 0 }}>
                                            <KpiTooltip
                                                isMobile={isMobile}
                                                text={`Includes ${fmt(r.refundedAfterTipOut)} in tips refunded after the tip period closed — may not match what was paid out.`}
                                            />
                                        </span>
                                    )}
                                </span>
                                <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexShrink: 0 }}>
                                    {showRate && <span style={{ fontSize: s.subtitle, color: T.t3 }}>{fmtRate(r.ratePct)}</span>}
                                    <span style={{ fontSize: s.rowVal, fontWeight: 600, color: r.amount < 0 ? T.red : T.t1, textAlign: 'right' }}>{fmt(r.amount)}</span>
                                </div>
                            </div>
                            <div style={{ height: s.bar, background: T.hover, borderRadius: 3, overflow: 'hidden' }}>
                                <div style={{ height: '100%', borderRadius: 3, width: `${max > 0 ? (r.amount / max) * 100 : 0}%`, background: barColor }} />
                            </div>
                        </div>
                    ))
                ) : (
                    <SectionEmpty kpiName={emptyName} size="small" />
                )}
            </div>
        );
    };

    const ChannelPanel = ({
        title,
        subtitle,
        rows,
        pctDecimals,
        emptyName,
        isLoading,
        isFailure,
        onRetry,
    }: {
        title: string;
        subtitle: string;
        rows: TgSection['channels'];
        /** Precision the section's percents were balanced to — render at exactly that. */
        pctDecimals: number;
        emptyName: string;
        // The two channel panels read different endpoints (Tips: by-channel; Gratuity:
        // staff-perfomance), so each carries its own feed's loading / error state.
        isLoading: boolean;
        isFailure: boolean;
        onRetry?: () => void;
    }) => (
        <div style={{ ...card, flex: isMobile ? undefined : 1, padding: s.pad, marginBottom: isMobile ? s.gap : 0 }}>
            <div style={{ fontSize: s.title, fontWeight: 600, color: T.t1, marginBottom: 2 }}>{title}</div>
            <div style={{ fontSize: s.subtitle, color: T.t3, marginBottom: 12 }}>{subtitle}</div>
            {isLoading ? (
                <div>
                    {[1, 2, 3, 4].map((i) => (
                        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: i < 4 ? `1px solid ${T.divider}` : 'none' }}>
                            <Bone h={12} r={3} style={{ width: `${40 + i * 10}%` }} />
                            <Bone w={50} h={12} r={3} />
                        </div>
                    ))}
                </div>
            ) : isFailure ? (
                <SectionError kpiName={emptyName} onRetry={onRetry} size="small" />
            ) : rows.length ? (
                rows.map((r, i) => (
                    <div key={`${r.channel}-${i}`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: isMobile ? '7px 0' : '10px 0', borderBottom: i < rows.length - 1 ? `1px solid ${T.divider}` : 'none' }}>
                        <span style={{ fontSize: s.row, color: T.t1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.channel}</span>
                        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexShrink: 0 }}>
                            <span style={{ fontSize: s.subtitle, color: T.t3 }}>{r.pct.toFixed(pctDecimals)}%</span>
                            <span style={{ fontSize: s.rowVal, fontWeight: 600, color: T.t1, textAlign: 'right' }}>{fmt(r.amount)}</span>
                        </div>
                    </div>
                ))
            ) : (
                <SectionEmpty kpiName={emptyName} size="small" />
            )}
        </div>
    );

    const panelRow: React.CSSProperties = isMobile
        ? { display: 'block' }
        : { display: 'flex', gap: 16, alignItems: 'flex-start' };

    const miniRow: React.CSSProperties = isMobile
        ? { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: s.gap, marginBottom: s.gap }
        : { display: 'flex', gap: 12, marginBottom: 14 };

    return (
        <div style={{ padding: isMobile ? '0 0 8px' : 4 }}>
            <div style={{ fontSize: isMobile ? 11 : 12, color: T.t3, marginBottom: isMobile ? 4 : 8 }}>
                {loading ? <Bone w={240} h={12} r={3} style={{ display: 'inline-block' }} /> : tgSubHeader(data, fmt, includeGratuity)}
            </div>

            {/* ── Section 1: Tips (voluntary only — auto-gratuity never enters this ranking) ── */}
            <SectionBanner text="Tips" />
            <div style={miniRow}>
                <MiniK label="Total Tips" value={fmt(data.tips.total)} color={T.green} />
                <MiniK label="Avg / Order" value={data.tips.avg == null ? '—' : fmt(data.tips.avg)} />
                <MiniK label="Tip Rate" value={`${data.tips.ratePct.toFixed(1)}%`} sub="of Net Sales" />
                <MiniK label="Orders w/ Tips" value={String(data.tips.orders)} />
            </div>
            <div style={panelRow}>
                <StaffPanel
                    title="Tips by Staff"
                    subtitle="Voluntary tips — performance ranking"
                    rows={data.tips.staff}
                    barColor={T.green}
                    showRate
                    emptyName="tips by staff"
                />
                {showChannels && (
                    <ChannelPanel
                        title="Tips by Channel"
                        subtitle="Share of total tips"
                        rows={data.tips.channels}
                        pctDecimals={data.tips.pctDecimals}
                        emptyName="tips by channel"
                        isLoading={channelLoading}
                        isFailure={channelFailure}
                        onRetry={onRetryChannel}
                    />
                )}
            </div>

            {/* ── Section 2: Gratuity — never hidden, so a zero period stays legible ── */}
            {includeGratuity && (
                <>
                    <SectionBanner text="Gratuity" />
                    <div style={miniRow}>
                        <MiniK label="Total Gratuity" value={fmt(data.gratuity.total)} color={T.orange} />
                        <MiniK label="Avg / Gratuity Order" value={data.gratuity.avg == null ? '—' : fmt(data.gratuity.avg)} />
                        <MiniK label="Gratuity Rate" value={`${data.gratuity.ratePct.toFixed(1)}%`} sub="of Net Sales" />
                        <MiniK label="Orders w/ Gratuity" value={String(data.gratuity.orders)} />
                    </div>
                    <div style={panelRow}>
                        <StaffPanel
                            title="Gratuity by Staff"
                            subtitle="Attributed to the check owner"
                            rows={data.gratuity.staff}
                            barColor={T.orange}
                            showRate={false}
                            emptyName="gratuity by staff"
                        />
                        {showChannels && (
                            <ChannelPanel
                                title="Gratuity by Channel"
                                subtitle="Share of total gratuity"
                                rows={data.gratuity.channels}
                                pctDecimals={data.gratuity.pctDecimals}
                                emptyName="gratuity by channel"
                                isLoading={channelLoading}
                                isFailure={channelFailure}
                                onRetry={onRetryChannel}
                            />
                        )}
                    </div>
                </>
            )}
        </div>
    );
};

export default TipsGratuityDrill;
